// AI Generated Test
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonLoadingService } from '@monsido/services/loading/services/loading.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MonModalService } from 'app/modules/modals/services/mon-modal/mon-modal.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import { SeoRepoService } from '@client/app/services/api/seo-repo/seo-repo.service';

@Component({
    selector: 'page-details-seo-information',
    standalone: false,
    template: `
        <div class="row">
            <div class="col-lg-48 page-details-information-site" *ngIf="selectedSeoIssue?.id">
                <div class="border-bottom py-4 px-3 d-flex">
                    <mon-table-policy-type
                        class="inline-block pr-3"
                        [color]="classificationColors[(selectedSeoIssue.ignored ? 'ignored' : selectedSeoIssue.classification)]"
                        [passed]="!selectedSeoIssue.ignored && selectedSeoIssue.fixed"
                        [target]="target"
                    >
                    </mon-table-policy-type>
                    <div class="side-panel-header">
                        {{ selectedSeoIssue.translation_name | translateSeoIssue }}
                    </div>
                </div>
                <div class="col-lg-48 border-bottom mb-4">
                    <page-details-sections-tabs [tab]="tab" [options]="tabOptions" (onTabChange)="onTabChange($event.tab)">
                        <div class="py-1">
                            <mon-action-menu [monActionMenuItems]="dropdownOptions">
                                <span translate>Action</span>
                            </mon-action-menu>
                            <mon-extension-visual-link-button [page]="page"
                                                          [type]="'seo'"
                                                          [data]="selectedSeoIssue"
                                                          [monDisabled]="!selectedSeoIssue || !selectedSeoIssue.issues || selectedSeoIssue.issues.length === 0"
                                                          class="pl-1">
                            </mon-extension-visual-link-button>
                        </div>
                    </page-details-sections-tabs>
                </div>
                <seo-information-information [page]="page" [seo]="selectedSeoIssue" *ngIf="tab === 'information'"></seo-information-information>
                <seo-information-quick-help [seo]="selectedSeoIssue" *ngIf="tab === 'quick-help'"></seo-information-quick-help>
            </div>
        </div>
    `,
})
export class PageDetailsSeoInformationComponent implements OnInit, OnChanges {
    @Input() selectedSeoIssue: any;
    @Input() page: any;

    @Output() getPageSeoIssues = new EventEmitter();

    policy: any = {};
    target = 'seo';
    classificationColors: any = {
        1: 'danger',
        2: 'warning',
        3: 'link',
        4: 'primary',
        ignored: 'grey',
    };
    dropdownOptions: any[] = [];
    tabOptions: any[] = [];
    tab: string = '';
    dialog: any;

    constructor (
        private translateService: TranslateService,
        private monLoadingService: MonLoadingService,
        private pageService: PageService,
        private seoRepoService: SeoRepoService,
        private monModalService: MonModalService,
        private monEventService: MonEventService,
    ) {}

    ngOnInit (): void {
        this.setupTabs();
    }

    ngOnChanges (changes: SimpleChanges): void {
        if (changes.selectedSeoIssue) {
            this.updateDropdown();
        }
    }

    private setupTabs (): void {
        this.tabOptions = [
            {
                name: this.translateService.getString('Information'),
                value: 'information',
            },
            {
                name: this.translateService.getString('Quick help'),
                value: 'quick-help',
            },
        ];

        this.tab = this.tabOptions[0].value;
    }

    onTabChange (tab: string): void {
        this.tab = tab;
    }

    private updateDropdown (): void {
        if (!this.selectedSeoIssue) {
            this.dropdownOptions = [];
            return;
        }

        const dropdown = [
            {
                label: this.translateService.getString('Ignore on this page'),
                leftIcon: 'faEyeSlash',
                action: () => {
                    const message = this.translateService.getString('Are you sure you want to ignore the selected issue on this page?');
                    this.promptConfirmation(message, () => {
                        this.updatePageIssues('ignored');
                    });
                },
                eventParams: { action: 'page-details-seo-ignore' },
                shouldShow: !this.selectedSeoIssue.ignored_on_page && this.selectedSeoIssue.issues?.length > 0,
            },
            {
                label: this.translateService.getString('Unignore on this page'),
                leftIcon: 'faEye',
                action: () => {
                    const message = this.translateService.getString('Are you sure you want to unignore the selected issue on this page?');
                    this.promptConfirmation(message, () => {
                        this.updatePageIssues('active');
                    });
                },
                eventParams: { action: 'page-details-seo-unignore' },
                shouldShow: this.selectedSeoIssue.ignored_on_page && this.selectedSeoIssue.issues?.length > 0,
            },
            {
                label: this.translateService.getString('Mark as fixed'),
                leftIcon: 'faCheck',
                action: () => {
                    const message = this.translateService.getString('Are you sure you want mark as fixed');
                    this.promptConfirmation(message, () => {
                        this.updatePageIssues('fixed');
                    });
                },
                eventParams: { action: 'page-details-seo-mark-fixed' },
                shouldShow: !this.selectedSeoIssue.fixed && !this.selectedSeoIssue.ignored,
            },
            {
                divider: true,
                shouldShow: !this.selectedSeoIssue.ignored && !this.selectedSeoIssue.fixed,
            },
            {
                label: this.translateService.getString('Ignore this check for this domain'),
                leftIcon: 'faEyeSlash',
                action: () => {
                    const message = this.translateService.getString('Are you sure you want to ignore this check for this domain?');
                    this.promptConfirmation(message, () => this.ignoreOnDomain());
                },
                eventParams: { action: 'page-details-seo-ignore-on-domain' },
                shouldShow: !this.selectedSeoIssue.ignored,
            },
            {
                label: this.translateService.getString('Unignore this check for this domain'),
                leftIcon: 'faEye',
                action: () => {
                    const message = this.translateService.getString('Are you sure you want to unignore this check for this domain?');
                    this.promptConfirmation(message, () => this.ignoreOnDomain());
                },
                eventParams: { action: 'page-details-seo-ignore-on-domain' },
                shouldShow: this.selectedSeoIssue.ignored,
            },
        ];

        this.dropdownOptions = dropdown.filter((option) => option.shouldShow !== false).map(item => {
            return {
                label: item.label,
                leftIcon: item.leftIcon,
                disabled: (item as any).disabled,
                divider: item.divider,
                action: () => {
                    if (item.eventParams) {
                        this.monEventService.run('actionTrigger', item.eventParams);
                    }

                    if (item.action) {
                        item.action();
                    }
                },
            };
        });
    }

    private promptConfirmation (message: string, callback: () => void): void {
        this.monModalService.confirm(message)
            .then(callback)
            .catch(() => {});
    }

    private updatePageIssues (status: string): void {
        const params = {
            status: status,
        };
        this.dialog = this.monLoadingService.show();
        const promises: any[] = [];

        this.selectedSeoIssue.issues.forEach((issue: any) => {
            promises.push(this.pageService.updateSeoIssue(this.page.id, issue.id, params));
        });

        Promise.all(promises)
            .then(() => {
                this.getPageSeoIssues.emit({ refreshPage: true });
            })
            .catch(() => {})
            .finally(() => this.closeLoadingDialog());
    }

    private updateAllIssues (status: string): void {
        const params = {
            status: status,
        };
        this.dialog = this.monLoadingService.show();

        this.pageService
            .updateSeoIssue(this.page.id, this.selectedSeoIssue.issues[0].id, params)
            .then(() => {
                this.getPageSeoIssues.emit({ refreshPage: true });
            })
            .catch(() => {})
            .finally(() => this.closeLoadingDialog());
    }

    private ignoreOnDomain (): void {
        const params: any = {
            ignored: !this.selectedSeoIssue.ignored,
        };
        this.dialog = this.monLoadingService.show();
        this.seoRepoService
            .update(this.selectedSeoIssue.id, params)
            .then(() => {
                this.getPageSeoIssues.emit({ refreshPage: true });
            })
            .catch(() => {})
            .finally(() => this.closeLoadingDialog());
    }

    private closeLoadingDialog (): void {
        setTimeout(() => {
            // The dialog isn't created immediately, if a there are no issues to wait on
            this.dialog?.close();
        }, 200);
    }
}
